import { Card, CardContent, CardHeader, CardTitle } from '@/common/ui/card'
import {
	BarChart,
	Bar,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	ResponsiveContainer,
	Cell,
} from 'recharts'

import type { OpensByDayWindowResponse } from '../../types'

interface OpensByDayWindowChartProps {
	opensByDayWindow: OpensByDayWindowResponse | undefined
	isLoading?: boolean
	title?: string
}

const COLORS = {
	day1: '#3b82f6', // blue
	day3: '#8b5cf6', // violet
	day7: '#22c55e', // green
	day14: '#f59e0b', // amber
	day30: '#ef4444', // red
}

function formatNumber(value: number): string {
	return value.toLocaleString('th-TH')
}

export default function OpensByDayWindowChart({
	opensByDayWindow,
	isLoading,
	title = 'การเปิดดูตามช่วงเวลา',
}: OpensByDayWindowChartProps) {
	if (isLoading) {
		return (
			<Card>
				<CardHeader>
					<CardTitle>{title}</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="h-64 animate-pulse rounded bg-muted" />
				</CardContent>
			</Card>
		)
	}

	if (!opensByDayWindow) {
		return (
			<Card>
				<CardHeader>
					<CardTitle>{title}</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="flex h-64 items-center justify-center">
						<p className="text-muted-foreground">ไม่มีข้อมูล</p>
					</div>
				</CardContent>
			</Card>
		)
	}

	const chartData = [
		{
			period: '1 วัน',
			opens: opensByDayWindow.day1,
			color: COLORS.day1,
		},
		{
			period: '3 วัน',
			opens: opensByDayWindow.day3,
			color: COLORS.day3,
		},
		{
			period: '7 วัน',
			opens: opensByDayWindow.day7,
			color: COLORS.day7,
		},
		{
			period: '14 วัน',
			opens: opensByDayWindow.day14,
			color: COLORS.day14,
		},
		{
			period: '30 วัน',
			opens: opensByDayWindow.day30,
			color: COLORS.day30,
		},
	]

	return (
		<Card>
			<CardHeader>
				<CardTitle>{title}</CardTitle>
				<p className="text-sm text-muted-foreground">
					จำนวนการเปิดดู (สะสม) ตามช่วงเวลาหลังจากได้รับข้อความ
				</p>
			</CardHeader>
			<CardContent>
				<ResponsiveContainer width="100%" height={300}>
					<BarChart
						data={chartData}
						margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
					>
						<CartesianGrid
							strokeDasharray="3 3"
							stroke="hsl(var(--border))"
							opacity={0.5}
						/>
						<XAxis
							dataKey="period"
							stroke="hsl(var(--muted-foreground))"
							tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
						/>
						<YAxis
							stroke="hsl(var(--muted-foreground))"
							tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
							tickFormatter={(value) => formatNumber(value)}
						/>
						<Tooltip
							contentStyle={{
								backgroundColor: 'hsl(var(--card))',
								border: '1px solid hsl(var(--border))',
								borderRadius: '8px',
								boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
							}}
							formatter={(value: number) => [
								formatNumber(value),
								'จำนวนเปิดดู',
							]}
							labelFormatter={(label) => `ภายใน ${label}`}
						/>
						<Bar dataKey="opens" name="เปิดดู" radius={[4, 4, 0, 0]}>
							{chartData.map((entry, index) => (
								<Cell key={`cell-${index}`} fill={entry.color} />
							))}
						</Bar>
					</BarChart>
				</ResponsiveContainer>
			</CardContent>
		</Card>
	)
}
